import { match, P } from 'ts-pattern';
import type { z } from 'zod';

import type { Session } from '@/entities/session';
import { getTreatmentApproachLabel } from '@/entities/treatment-approach';
import { formatLongDate, parseLocalDate } from '@/shared/lib/time-utils';

import type { nessoNoteContentSchema } from '../model/session-note.schemas';

type NessoContent = z.infer<typeof nessoNoteContentSchema>;
type Intervention = NessoContent['interventions'][number];
type ManagingSymptoms = NonNullable<NessoContent['progress']['managingSymptoms']>[number];
type NextSession = NonNullable<NessoContent['plan']['nextSession']>;

export function formatNessoNote(session?: Session): string | null {
    const note = session?.currentNote;
    if (!session || !note || note.type !== 'NESSO' || !note.content) return null;

    const sections = [
        formatChiefComplaintSection(note.content.chiefComplaint),
        formatClientPresentationSection(note.content.clientPresentation),
        formatRiskSection(note.content.risk),
        formatInterventionSection(note.content.interventions),
        formatProgressSection(note.content.progress),
        formatPlanSection(note.content.plan),
        formatAdditionalDetailsSection(note.content.additionalNotes),
    ].filter((section): section is string => section !== null);

    return sections.join('\n\n');
}

function formatChiefComplaintSection(complaint: NessoContent['chiefComplaint']): string | null {
    const blocks: string[] = [];

    const emotions = [...(complaint.emotion ?? []), ...splitOther(complaint.emotionOther)];
    const about = complaint.about?.trim();
    const intro = formatChiefComplaintIntro(emotions, about);
    if (intro) blocks.push(intro);

    const quote = complaint.directQuote?.trim();
    if (quote) blocks.push(`Client stated, “${quote}”`);

    const relatedTo = [...(complaint.relatedTo ?? []), ...splitOther(complaint.relatedToOther)];
    if (relatedTo.length > 0) {
        blocks.push(
            ['Discussed thoughts, actions, and feelings related to:', ...toBullets(relatedTo)].join(
                '\n',
            ),
        );
    }

    if (blocks.length === 0) return null;

    return `CHIEF COMPLAINT\n${blocks.join('\n\n')}`;
}

function formatChiefComplaintIntro(emotions: string[], about: string | undefined): string | null {
    const emotionList = formatList(emotions.map((e) => e.toLowerCase()));

    if (emotionList && about) {
        return `Client reported feeling ${emotionList} about ${about}.`;
    }
    if (emotionList) {
        return `Client reported feeling ${emotionList}.`;
    }
    if (about) {
        return `Client talked about ${about}.`;
    }
    return null;
}

function formatClientPresentationSection(
    presentation: NessoContent['clientPresentation'],
): string | null {
    const observations = [
        ...(presentation.mildLow ?? []),
        ...splitOther(presentation.mildLowOther),
        ...(presentation.moderate ?? []),
        ...splitOther(presentation.moderateOther),
        ...(presentation.highSevere ?? []),
        ...splitOther(presentation.highSevereOther),
    ];

    if (observations.length === 0) return null;

    return [
        'CLIENT PRESENTATION',
        'Clinician observed mood, affect, behavior:',
        ...toBullets(observations),
    ].join('\n');
}

function formatRiskSection(risk: NessoContent['risk']): string {
    const noRiskSection = 'RISK\nNo risk reported or observed';
    if (!risk) return noRiskSection;

    const blocks: Array<{ title: string; body: string }> = [];

    const areaOfRisk = risk.areaOfRisk ?? [];
    if (areaOfRisk.length > 0) {
        blocks.push({ title: 'Area of risk', body: toBullets(areaOfRisk).join('\n') });
    }

    const disposition = risk.disposition ?? [];
    if (disposition.length > 0) {
        blocks.push({ title: 'Disposition', body: toBullets(disposition).join('\n') });
    }

    const protectiveFactors = risk.protectiveFactors ?? [];
    if (protectiveFactors.length > 0) {
        blocks.push({
            title: 'Protective factors',
            body: toBullets(protectiveFactors).join('\n'),
        });
    }

    const riskFactors = risk.riskFactors ?? [];
    if (riskFactors.length > 0) {
        blocks.push({ title: 'Risk factors', body: toBullets(riskFactors).join('\n') });
    }

    const description = risk.descriptionOfRisk?.trim();
    if (description) {
        blocks.push({ title: 'Description of risk', body: description });
    }

    const safetyPlan = risk.safetyPlan?.trim();
    if (safetyPlan) {
        blocks.push({ title: 'Safety plan', body: safetyPlan });
    }

    if (blocks.length === 0) return noRiskSection;

    if (blocks.length === 1) {
        return `RISK\n${blocks[0].body}`;
    }

    return `RISK\n${blocks.map((block) => `${block.title}\n${block.body}`).join('\n\n')}`;
}

function formatInterventionSection(interventions: NessoContent['interventions']): string | null {
    const blocks = interventions
        .map((entry) => formatInterventionEntry(entry))
        .filter((entry): entry is string => entry !== null);

    if (blocks.length === 0) return null;

    const entriesSeparator = blocks.length > 1 ? '\n\n\n' : '\n\n';
    return `INTERVENTION\n${blocks.join(entriesSeparator)}`;
}

function formatInterventionEntry(entry: Intervention): string | null {
    const interventionsList = formatList(entry.interventions ?? []);
    const label = getTreatmentApproachLabel(entry.treatmentApproach);

    const heading = interventionsList
        ? `${label} techniques, including ${interventionsList} in order to:`
        : `${label} techniques in order to:`;

    const goals = [...(entry.goals ?? []), ...splitOther(entry.otherGoal)];

    if (goals.length === 0) return null;

    return [heading, ...toBullets(goals)].join('\n');
}

function formatProgressSection(progress: NessoContent['progress']): string | null {
    const blocks: string[] = [];

    const progressLabel = progress.progressOther?.trim() ?? progress.progress;
    if (progressLabel) {
        blocks.push(`In general, client is ${lowercaseFirst(progressLabel)}.`);
    }

    const improvements = [
        ...(progress.improvement ?? []),
        ...splitOther(progress.improvementOther),
    ];
    if (improvements.length > 0) {
        blocks.push(['Client shows improvement in:', ...toBullets(improvements)].join('\n'));
    }

    const symptoms = formatManagingSymptoms(
        progress.managingSymptoms ?? [],
        splitOther(progress.managingSymptomsOther),
    );
    if (symptoms) {
        blocks.push(['Client shows progress managing symptoms of:', symptoms].join('\n'));
    }

    const struggles = [
        ...(progress.continuesToStruggle ?? []),
        ...splitOther(progress.continuesToStruggleOther),
    ];
    if (struggles.length > 0) {
        blocks.push(['Client continues to struggle with:', ...toBullets(struggles)].join('\n'));
    }

    const impacts = [
        ...(progress.impactsToDailyLife ?? []),
        ...splitOther(progress.impactsToDailyLifeOther),
    ];
    if (impacts.length > 0) {
        blocks.push(['Impacts to daily life:', ...toBullets(impacts)].join('\n'));
    }

    const barriers = progress.barriersToProgress?.trim();
    if (barriers) {
        blocks.push(['New barriers to progress:', barriers].join('\n'));
    }

    if (blocks.length === 0) return null;

    return `PROGRESS\n${blocks.join('\n\n')}`;
}

function formatManagingSymptoms(items: ManagingSymptoms[], other: string[]): string | null {
    const lines = [
        ...items.flatMap((item) => {
            const symptoms = [...(item.symptoms ?? []), ...splitOther(item.other)];
            if (symptoms.length === 0) return toBullets([item.symptomCategory]);
            return toBullets(symptoms);
        }),
        ...toBullets(other),
    ];

    return lines.length > 0 ? lines.join('\n') : null;
}

function formatPlanSection(plan: NessoContent['plan']): string | null {
    const blocks: string[] = [];

    const nextSession = formatNextSession(plan.nextSession);
    if (nextSession) blocks.push(nextSession);

    const homework = [...(plan.homework ?? []), ...splitOther(plan.homeworkOther)];
    if (homework.length > 0) {
        blocks.push(['Homework:', ...homework].join('\n'));
    }

    const resources = plan.resourcesAndRecommendations?.trim();
    if (resources) {
        blocks.push(['Resources and recommendations:', resources].join('\n'));
    }

    const followUp = plan.followUpOn?.trim();
    if (followUp) {
        blocks.push(['Follow-up:', followUp].join('\n'));
    }

    if (blocks.length === 0) return null;

    return `PLAN\n${blocks.join('\n\n')}`;
}

function formatNextSession(nextSession: NextSession | undefined): string | null {
    if (!nextSession) return null;

    return match(nextSession)
        .with({ nextSession: 'Specify date', date: P.string.minLength(1) }, ({ date }) => {
            return `Next session is scheduled for ${formatLongDate(parseLocalDate(date))}.`;
        })
        .with({ nextSession: 'Specify date' }, () => null)
        .with({ nextSession: 'Next week' }, () => 'Next session is scheduled for next week.')
        .with(
            { nextSession: 'Client will reach out to schedule' },
            () => 'Client will reach out to schedule.',
        )
        .with(
            { nextSession: 'No further treatment necessary' },
            () => 'No further treatment necessary.',
        )
        .exhaustive();
}

function formatAdditionalDetailsSection(notes: string | undefined): string | null {
    const trimmed = notes?.trim();
    if (!trimmed) return null;

    return ['ADDITIONAL DETAILS', trimmed].join('\n');
}

function toBullets(items: readonly string[]): string[] {
    return items.map((item) => `• ${item}`);
}

function splitOther(value: string | undefined): string[] {
    const trimmed = value?.trim();
    return trimmed ? [trimmed] : [];
}

function formatList(items: readonly string[]): string | null {
    if (items.length === 0) return null;
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

function lowercaseFirst(value: string): string {
    return value.charAt(0).toLowerCase() + value.slice(1);
}
